if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    connectedCallback() {
      this.wildousFrame = requestAnimationFrame(() => this.initializeColourGroups());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.wildousFrame);
    }

    initializeColourGroups() {
      if (this.colourGroups) return;
      const configElement = this.querySelector('[data-wildous-colour-gallery]');
      if (!configElement) return;
      let config;
      try { config = JSON.parse(configElement.textContent); } catch (error) { return; }
      if (config.colourIndex < 0 || !config.mediaIds.length) return;
      const colours = new Set(config.variants.map(variant => variant.options[config.colourIndex]));
      if (colours.size < 2) return;
      const anchors = new Map();
      const mappedColours = new Set();
      for (const variant of config.variants) {
        const colour = variant.options[config.colourIndex];
        const mediaId = String(variant.mediaId || '');
        if (!config.mediaIds.some(id => String(id) === mediaId)) continue;
        if (anchors.has(mediaId) && anchors.get(mediaId) !== colour) return;
        anchors.set(mediaId, colour);
        mappedColours.add(colour);
      }
      // Do not guess when a colour has no image anchor or the gallery starts with unassigned media.
      if (mappedColours.size !== colours.size || !anchors.has(String(config.mediaIds[0]))) return;
      this.colourGroups = new Map();
      let currentColour;
      config.mediaIds.forEach(id => {
        const key = String(id);
        if (anchors.has(key)) currentColour = anchors.get(key);
        this.colourGroups.set(key, currentColour);
      });
      this.colourConfig = config;
      this.mediaPrefix = this.id.replace(/^MediaGallery-/, '').replace(/-duplicate$/, '');
      const selected = config.variants.find(variant => String(variant.id) === String(config.selectedVariantId));
      if (selected && selected.mediaId) {
        this.filterColourGroup(String(selected.mediaId));
      }
    }

    filterColourGroup(mediaId) {
      if (!this.colourGroups || !this.colourGroups.has(mediaId)) return;
      const colour = this.colourGroups.get(mediaId);
      const visibleIds = new Set();
      const list = this.elements.viewer.querySelector('[id^="Slider-Gallery-"]');
      if (!list) return;
      // Restore original product-media order after variant changes have prepended an image.
      this.colourConfig.mediaIds.forEach(id => {
        const item = list.querySelector(`[data-media-id="${this.mediaPrefix}-${id}"]`);
        if (item) list.appendChild(item);
      });
      list.querySelectorAll('[data-media-id]').forEach(item => {
        const id = item.dataset.mediaId.slice(this.mediaPrefix.length + 1);
        const visible = this.colourGroups.get(id) === colour;
        item.hidden = !visible;
        item.style.setProperty('display', visible ? 'block' : 'none', 'important');
        item.classList.remove('is-active');
        if (visible) visibleIds.add(id);
      });
      const active = list.querySelector(`[data-media-id="${this.mediaPrefix}-${mediaId}"]`);
      if (active) active.classList.add('is-active');
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach(item => {
          const id = item.dataset.target.slice(this.mediaPrefix.length + 1);
          const visible = visibleIds.has(id);
          item.hidden = !visible;
          item.style.setProperty('display', visible ? 'block' : 'none', 'important');
        });
      }
      const modal = document.getElementById(`ProductModal-${this.mediaPrefix}`);
      if (modal) {
        modal.querySelectorAll('.product-media-modal__content [data-media-id]').forEach(item => {
          const visible = visibleIds.has(String(item.dataset.mediaId));
          item.hidden = !visible;
          if (!visible) item.style.setProperty('display', 'none', 'important');
          else item.style.removeProperty('display');
        });
      }
      this.refreshColourSlider(this.elements.viewer);
      if (this.elements.thumbnails) this.refreshColourSlider(this.elements.thumbnails);
      this.elements.viewer.querySelectorAll('.slider-counter--current').forEach(item => { item.textContent = '1'; });
      this.elements.viewer.querySelectorAll('.slider-counter--total').forEach(item => { item.textContent = String(visibleIds.size); });
      if (this.elements.liveRegion) this.elements.liveRegion.textContent = colour;
    }

    refreshColourSlider(slider) {
      if (!slider || !slider.slider) return;
      // The original slider assumes at least two visible slides. Handle single-image colours safely.
      if (!slider.wildousOriginalInitPages) {
        slider.wildousOriginalInitPages = slider.initPages;
        slider.initPages = function() {
          this.sliderItemsToShow = Array.from(this.sliderItems).filter(item => item.clientWidth > 0 && !item.hidden);
          if (this.sliderItemsToShow.length < 2) {
            this.sliderItemOffset = this.slider.clientWidth || 1;
            this.slidesPerPage = 1;
            this.totalPages = Math.max(1, this.sliderItemsToShow.length);
            this.currentPage = 1;
            if (this.currentPageElement) this.currentPageElement.textContent = '1';
            if (this.pageTotalElement) this.pageTotalElement.textContent = String(this.totalPages);
            if (this.prevButton) this.prevButton.disabled = true;
            if (this.nextButton) this.nextButton.disabled = true;
            return;
          }
          this.wildousOriginalInitPages();
        };
      }
      slider.slider.scrollLeft = 0;
      slider.resetPages();
    }

    onSlideChanged(event) {
      if (!event.detail.currentElement || !this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      this.initializeColourGroups();
      if (prepend && this.colourGroups) {
        this.filterColourGroup(mediaId.slice(this.mediaPrefix.length + 1));
        prepend = false;
      }
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia || activeMedia.hidden) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
